import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getAuditLogReport } from '../../data/reportHelpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiList, FiSearch, FiCheckCircle, FiAlertCircle } = FiIcons;

const COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'learnerName', label: 'Learner' },
  { key: 'department', label: 'Department' },
  { key: 'action', label: 'Action' },
  { key: 'details', label: 'Details' },
  { key: 'ipAddress', label: 'IP Address' },
];

const AuditLogReport = ({ filters, raw }) => {
  const data = useMemo(() => getAuditLogReport(raw, filters), [raw, filters]);
  const [search, setSearch] = useState('');

  const filtered = data.filter(r =>
    !search || 
    r.learnerName.toLowerCase().includes(search.toLowerCase()) || 
    r.action.toLowerCase().includes(search.toLowerCase()) ||
    r.details.toLowerCase().includes(search.toLowerCase())
  );

  const stats = [
    { label: 'Total Events', value: data.length, icon: FiList, iconColor: 'bg-indigo-50 text-indigo-600', border: 'border-indigo-100' },
    { label: 'Completions', value: data.filter(r => r.action.toLowerCase().includes('complete')).length, icon: FiCheckCircle, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Alerts / Failures', value: data.filter(r => r.action.toLowerCase().includes('fail') || r.action.toLowerCase().includes('click')).length, icon: FiAlertCircle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
  ];

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="relative max-w-sm">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search audit logs…"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
        </div>
      </div>
      <ExportBar reportTitle="Audit Log Report" data={filtered} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={filtered.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Date</th>
                <th className="p-4">Learner</th>
                <th className="p-4">Department</th>
                <th className="p-4">Action</th>
                <th className="p-4">Details</th>
                <th className="p-4">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map((row, i) => (
                <tr key={row.id || i} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 text-xs text-slate-600 whitespace-nowrap">
                    {row.date !== '—' ? new Date(row.date).toLocaleString() : '—'}
                  </td>
                  <td className="p-4 font-medium text-slate-800">{row.learnerName}</td>
                  <td className="p-4 text-xs text-slate-600">{row.department}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
                      {row.action}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-slate-500 max-w-xs truncate" title={row.details}>
                    {row.details}
                  </td>
                  <td className="p-4 text-xs text-slate-400 font-mono">
                    {row.ipAddress}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">{filtered.length} of {data.length} events</div>
      </div>
    </div>
  );
};

export default AuditLogReport;